import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, StatusChip, cn, fmtRelativeTime } from '@smena/ui';
import { QueryState } from '@/components/QueryState';
import { fetchSessions, fetchThread } from './api';

// Read-only. Отвечать гостю из админки нельзя намеренно: impersonation вынесен в V2,
// а чтение переписки закрывает большую часть вопросов «почему бот ответил вот так».

export function DialogsTab({ accountId }: { accountId: string }) {
  const [sid, setSid] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['sessions', accountId],
    queryFn: () => fetchSessions(accountId),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <Card className="max-h-[70vh] overflow-auto">
        <QueryState
          isLoading={list.isLoading}
          error={list.error}
          isEmpty={list.data?.length === 0}
          emptyText="Диалогов нет"
        >
          <ul>
            {list.data?.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setSid(s.sid)}
                  className={cn(
                    'w-full border-b border-line/60 px-4 py-3 text-left transition-colors hover:bg-panel2',
                    sid === s.sid && 'bg-accent/15',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm">{s.guestName ?? 'без имени'}</span>
                    {s.humanTookOver ? <StatusChip tone="amber">оператор</StatusChip> : null}
                  </div>
                  <div className="truncate font-mono text-[11px] text-muted">{s.channelRef}</div>
                  <div className="text-[11px] text-muted">
                    {s.messagesCount} сообщ. · {fmtRelativeTime(s.lastMessageAt)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>

      {sid ? (
        <Thread accountId={accountId} sid={sid} />
      ) : (
        <Card className="grid place-items-center p-8 text-sm text-muted">
          Выберите диалог слева. Открытие нити пишется в admin_audit.
        </Card>
      )}
    </div>
  );
}

function Thread({ accountId, sid }: { accountId: string; sid: string }) {
  const q = useQuery({
    queryKey: ['thread', accountId, sid],
    queryFn: () => fetchThread(accountId, sid),
    // Не рефетчим по фокусу и не поллим: каждый запрос - запись «смотрел ПДн» в журнале.
    staleTime: Infinity,
  });

  return (
    <Card className="flex max-h-[70vh] flex-col">
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data ? (
          <>
            <div className="shrink-0 border-b border-line px-4 py-3 text-sm">
              <div className="flex items-center gap-2">
                <span>{q.data.session.guestName ?? 'без имени'}</span>
                {q.data.session.guestPhone ? (
                  <span className="font-mono text-xs text-muted">{q.data.session.guestPhone}</span>
                ) : null}
                {q.data.session.humanTookOver ? <StatusChip tone="amber">оператор</StatusChip> : null}
              </div>
              <div className="font-mono text-[11px] text-muted">{q.data.session.sid}</div>
            </div>
            <div className="flex flex-1 flex-col gap-2 overflow-auto p-4">
              {q.data.messages.map((m) => (
                <div
                  key={m.id}
                  className={cn(
                    'max-w-[75%] rounded-lg px-3 py-2 text-sm',
                    m.role === 'user'
                      ? 'self-start bg-panel2'
                      : m.role === 'assistant'
                        ? 'self-end bg-accent/20'
                        : 'self-center bg-line/40 text-xs text-muted',
                  )}
                >
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                  <div className="mt-1 text-[10px] text-muted">
                    {new Date(m.ts).toLocaleString('ru-RU')}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </QueryState>
    </Card>
  );
}
